import { Alert, CircularProgress } from "@mui/material";
import * as React from "react";
import { Link, useParams } from "react-router-dom";

import { ReadOnlyAIChatProvider } from "@/Admin/ExploreChats/ReadOnlyAIChatProvider";
import { AIChatProvider } from "@/Chat/ChatSidebar/AIChatContext";
import { Chat } from "@/Chat/ChatSidebar/Chat";
import { ChatByIdView } from "@/Chat/ChatSidebar/ChatByIdView";
import { useNavigation } from "@/providers";
import { useAuth } from "@/providers/auth/hooks";
import { PageBreadcrumbs, PageLayout } from "@/shared/components";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import { useAgentSession } from "@/shared/domain/agents/hooks";
import { RobotoDomainException } from "@/shared/http";
import { Goto } from "@/shared/navigation";

import styles from "./AgentSessionDetail.module.css";

/**
 * Detail page for an agent session at ``/agents/sessions/:sessionId``.
 *
 * Mirrors :class:`AdminChatViewer` for non-admin users. Branches on
 * ownership inside :func:`AgentSessionContent`:
 *
 * - **Owner** (``chat.created_by`` matches the signed-in user): render
 *   the interactive ``<Chat />`` so the user can continue the
 *   conversation.
 * - **Non-owner** (a session visible to the user but created by a
 *   teammate): render ``<ChatByIdView />`` inside
 *   ``ReadOnlyAIChatProvider``. Writes are no-ops; the user can fork
 *   from any assistant message to land on their own copy.
 *
 * Loading and error states flow through ``<React.Suspense>`` and
 * ``<ErrorBoundary>`` rather than inline ``isLoading`` / ``error``
 * branching. The chat fetch lives inside :func:`AgentSessionContent`
 * (and :func:`AgentSessionTitleSuffix` for the breadcrumb title), both
 * of which call :func:`useAgentSession` and suspend until the response
 * is available. A 4xx response surfaces the server's message; anything
 * else falls back to a generic copy so we don't leak server-side
 * details.
 */
export function AgentSessionDetail() {
  const { sessionId } = useParams<{ sessionId: string }>();

  if (sessionId === undefined) {
    // Programming error — the route declaration registers
    // ``/agents/sessions/:sessionId`` so React Router guarantees the
    // param is set by the time this component mounts.
    throw new Error("sessionId not provided");
  }

  return (
    <PageLayout
      title={`Roboto - Agent session - ${sessionId}`}
      headerContentLeft={
        // The fallback omits the title suffix so the breadcrumb is
        // stable before the chat resolves. Once the suspense boundary
        // unblocks, the rendered breadcrumb appends ``- ${title}`` when
        // the session has one.
        <React.Suspense
          fallback={<AgentSessionBreadcrumb sessionId={sessionId} />}
        >
          <AgentSessionBreadcrumb sessionId={sessionId} includeTitle />
        </React.Suspense>
      }
    >
      <ErrorBoundary
        fallback={({ error }) => <AgentSessionErrorAlert error={error} />}
      >
        <React.Suspense fallback={<AgentSessionLoading />}>
          <AgentSessionContent sessionId={sessionId} />
        </React.Suspense>
      </ErrorBoundary>
    </PageLayout>
  );
}

interface AgentSessionBreadcrumbProps {
  sessionId: string;
  /**
   * When true, suspends on :func:`useAgentSession` and appends ``-
   * ${title}`` to the session id when the loaded record has a title.
   * Defaults to false so the same component can be reused as the
   * Suspense fallback without triggering a second network request or
   * re-suspending.
   */
  includeTitle?: boolean;
}

function AgentSessionBreadcrumb({
  sessionId,
  includeTitle = false,
}: AgentSessionBreadcrumbProps) {
  return (
    <PageBreadcrumbs>
      <Link to={Goto.route.Agents}>Agent Sessions</Link>
      <span>
        {sessionId}
        {includeTitle && <AgentSessionTitleSuffix sessionId={sessionId} />}
      </span>
    </PageBreadcrumbs>
  );
}

function AgentSessionTitleSuffix({ sessionId }: { sessionId: string }) {
  const { data: chat } = useAgentSession({ sessionId });
  if (!chat.title) {
    return null;
  }
  return <> - {chat.title}</>;
}

function AgentSessionLoading() {
  return (
    <div className={styles.loadingContainer}>
      <CircularProgress size="1.5rem" />
    </div>
  );
}

function AgentSessionErrorAlert({ error }: { error: unknown }) {
  // 4xx errors are user-actionable (session not found, forbidden, etc.)
  // — surface the server's message verbatim. Anything else (5xx,
  // network failures, framework errors) gets the generic copy so we
  // don't leak server-side details and the user has a contact path.
  if (error instanceof RobotoDomainException && error.isClientError()) {
    return <Alert severity="error">{error.message}</Alert>;
  }
  return (
    <Alert severity="error">
      Failed to load session. Please contact{" "}
      <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this problem
      persists.
    </Alert>
  );
}

function AgentSessionContent({ sessionId }: { sessionId: string }) {
  const { goto } = useNavigation();
  const { currentUser } = useAuth();
  const { data: chat } = useAgentSession({ sessionId });

  // Hold the owner/non-owner decision until the signed-in user is
  // loaded. ``currentUser`` is ``null`` while authentication is still
  // resolving, so checking ``chat.created_by`` against it too early
  // would briefly flash the read-only viewer on the user's own
  // session.
  const ownerCheckResolved = currentUser !== null;
  const isOwnSession =
    ownerCheckResolved && chat.created_by === currentUser.user_id;

  // Keep the URL in sync when the active chat id changes from inside
  // the interactive branch — e.g. when the user starts a brand-new
  // conversation from this page instead of continuing the loaded
  // session. Without this, reload and the browser back button would
  // point at the previous session.
  const handleChatIdChanged = React.useCallback(
    (nextId: string | undefined) => {
      if (nextId && nextId !== chat.session_id) {
        goto.agentSession({ sessionId: nextId });
      }
    },
    [chat.session_id, goto],
  );

  // When the user forks a read-only session, navigate this page to the
  // fork's detail URL so they land on their own copy and can continue
  // the conversation there.
  const handleForkNavigated = React.useCallback(
    (nextId: string) => {
      goto.agentSession({ sessionId: nextId });
    },
    [goto],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.metadata}>
          {/* Session id renders before title so it's the first thing a
              user can copy when grabbing context for a bug report or
              support thread. */}
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Session ID:</span>
            <span className={styles.metadataValue}>{chat.session_id}</span>
          </div>
          {chat.title && (
            <div className={styles.metadataItem}>
              <span className={styles.metadataLabel}>Title:</span>
              <span className={styles.metadataValue}>{chat.title}</span>
            </div>
          )}
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Created by:</span>
            <span className={styles.metadataValue}>{chat.created_by}</span>
          </div>
          <div className={styles.metadataItem}>
            <span className={styles.metadataLabel}>Created:</span>
            <span className={styles.metadataValue}>
              {new Date(chat.created).toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.chatContent}>
        {ownerCheckResolved && isOwnSession && (
          // Keying ``AIChatProvider`` by ``session_id`` forces a remount
          // when the user navigates between their own sessions. The
          // provider captures ``initialChatId`` in a lazy state
          // initializer that runs only at mount, so swapping the prop
          // alone would not change the active chat — a remount is the
          // supported path.
          <AIChatProvider
            key={chat.session_id}
            initialChatId={chat.session_id}
            onChatIdChanged={handleChatIdChanged}
          >
            <Chat />
          </AIChatProvider>
        )}
        {ownerCheckResolved && !isOwnSession && (
          <ReadOnlyAIChatProvider onChatClicked={handleForkNavigated}>
            <ChatByIdView chat={chat} />
          </ReadOnlyAIChatProvider>
        )}
      </div>

      {ownerCheckResolved && !isOwnSession && (
        <div className={styles.readOnlyBanner}>
          Read-only view of someone else&rsquo;s session. Fork from any
          assistant message to continue the conversation in your own thread.
        </div>
      )}
    </div>
  );
}
